import { useLearningUiTranslation } from '@features/learning/shared/use-learning-ui-translation'
import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useFlashcardsState } from '@features/flashcards/state/useFlashcardsState'
import { prefetchFlashcardCards } from '@features/flashcards/shared/flashcard-query'
import { AppEmptyState, AppErrorState, AppLoadingState, LmsButton } from '@shared/ui'
import { FlashcardCategoryCard } from './FlashcardCategoryCard'
import './flashcard-category.css'
import '../shared/flashcard-feature-sub-header.css'

export function FlashcardCategoryScreen() {
  const ui = useLearningUiTranslation()
  const { actions, state } = useFlashcardsState()
  const queryClient = useQueryClient()
  const isCategoriesPreparing =
    state.loading.categories || (!state.categoriesLoaded && !state.errorMessage)
  const handlePrefetchCategory = useCallback(
    (categoryId: string) => {
      if (Object.prototype.hasOwnProperty.call(state.cardsByCategoryId, categoryId)) {
        return
      }

      void prefetchFlashcardCards({ categoryId, queryClient })
    },
    [queryClient, state.cardsByCategoryId],
  )

  return (
    <>
      <header className="flashcardFeatureSubHeader flashcardCategorySubHeader">
        <div className="flashcardFeatureSubHeaderInner">
          <div className="flashcardFeatureSubHeaderCopy">
            <h1 className="flashcardFeatureSubHeaderTitle">{ui("Flashcard categories")}</h1>
          </div>
        </div>
      </header>
      <div className="flashcardCategorySurface">

      {isCategoriesPreparing ? (
        <AppLoadingState
          message={ui("Keep this page open while we load the categories you can practice.")}
          minHeight={240}
          title={ui("Preparing flashcard categories")}
        />
      ) : null}

      {!isCategoriesPreparing && state.errorMessage ? (
        <div className="flashcardCategoryErrorState">
          <AppErrorState
            message={state.errorMessage}
            title={ui("We could not load flashcard categories")}
          />
          <div className="flashcardCategoryErrorActions">
            <LmsButton
              onClick={() => {
                actions.clearErrorMessage()
              }}
              variant="outline"
            >
              {ui("Dismiss")}</LmsButton>
          </div>
        </div>
      ) : null}

      {!isCategoriesPreparing &&
      state.categoriesLoaded &&
      !state.errorMessage &&
      state.categories.length === 0 ? (
        <AppEmptyState
          message={ui("The categories request completed, but no published flashcard categories are available yet.")}
          minHeight={220}
          title={ui("No flashcard categories are available yet")}
        />
      ) : null}

      {!isCategoriesPreparing &&
      state.categoriesLoaded &&
      !state.errorMessage &&
      state.categories.length > 0 ? (
        <div
          className="flashcardCategoryGrid"
        >
          {state.categories.map((category) => (
            <FlashcardCategoryCard
              category={category}
              key={category.id}
              onPrefetch={() => {
                handlePrefetchCategory(category.id)
              }}
              onSelect={() => {
                actions.setActiveCategory(category)
              }}
            />
          ))}
        </div>
      ) : null}
      </div>
    </>
  )
}
